import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';

import { getSupplierView, getSupplierViewById } from '@/api/findings';
import { getRun } from '@/api/runs';
import { SupplierViewBody, SupplierViewError } from '@/components/SupplierDrawer';
import { Breadcrumbs } from '@/components/ui/breadcrumb';
import { Spinner } from '@/components/ui/spinner';

import { useAuth } from '../auth/AuthContext';

/** Shared body for the standalone supplier view page. Fetches by the
 *  stable `suppliers` row id when we have one (safe against a `/` in the
 *  name), otherwise falls back to the name-keyed endpoint for suppliers
 *  with no id (legacy ingests / the "(unknown supplier)" bucket).
 *
 *  Uses `SupplierViewBody` under the hood so it matches the drawer, but
 *  routed as its own URL for browser history / share / refresh. Back is
 *  `window.history.back()`; the link falls back to the run page on a
 *  cold deep-link. */
function SupplierViewContent({
  id,
  supplierId,
  supplierNameFromUrl,
}: {
  id: string;
  supplierId?: string;
  supplierNameFromUrl?: string;
}) {
  const { isSignedIn, userKey, getIdToken } = useAuth();
  const byId = supplierId !== undefined;

  const query = useQuery({
    queryKey: [
      'supplier-view',
      id,
      byId ? `id:${supplierId}` : `name:${supplierNameFromUrl}`,
      userKey,
    ],
    enabled: isSignedIn,
    // Journal entries load separately (INP-517), so the shell paints first.
    queryFn: async () => {
      const token = (await getIdToken()) ?? '';
      return byId
        ? getSupplierViewById(id, supplierId, token, false)
        : getSupplierView(id, supplierNameFromUrl ?? '', token, false);
    },
  });

  // Customer slug enables the 📎 invoice chips on the findings table.
  // Shares the run page's query key, so navigating from a run hits cache.
  const run = useQuery({
    queryKey: ['runs', id, userKey],
    enabled: isSignedIn,
    queryFn: async () => getRun(id, (await getIdToken()) ?? ''),
  });

  // The name-keyed page knows the name up front; the id-keyed page reads
  // the canonical name off the response once it loads.
  const displayName = query.data?.supplier_name ?? supplierNameFromUrl ?? '';

  return (
    <div className="grid gap-4">
      <header className="grid gap-2">
        <Breadcrumbs>
          <Link to="/customers" className="text-brand-royal underline-offset-4 hover:underline">
            Customers
          </Link>
          {/* Parent links only render for engagement-linked runs; CLI-triggered
              runs ship null for these fields. Mirrors the finding-detail breadcrumb. */}
          {run.data?.customer_slug && run.data?.customer_name && (
            <Link
              to="/customers/$slug"
              params={{ slug: run.data.customer_slug }}
              className="text-brand-royal underline-offset-4 hover:underline"
            >
              {run.data.customer_name}
            </Link>
          )}
          {run.data?.engagement_id && run.data?.engagement_name && (
            <Link
              to="/engagements/$id"
              params={{ id: run.data.engagement_id }}
              className="text-brand-royal underline-offset-4 hover:underline"
            >
              {run.data.engagement_name}
            </Link>
          )}
          <Link
            to="/runs/$id"
            params={{ id }}
            className="text-brand-royal underline-offset-4 hover:underline"
          >
            Recovery
          </Link>
          <span className="text-brand-navy">Supplier</span>
        </Breadcrumbs>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => window.history.back()}
            className="inline-flex items-center gap-1 rounded-md border border-input bg-background px-2.5 py-1 text-sm text-brand-royal shadow-xs hover:bg-muted/50 hover:text-brand-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
            aria-label="Go back to previous page"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Back
          </button>
        </div>
      </header>

      <header>
        <p className="text-xs uppercase tracking-wide text-brand-slate">Supplier</p>
        <h2 className="text-2xl font-semibold tracking-tight text-brand-navy">
          {displayName}
        </h2>
      </header>

      <div className="grid gap-6">
        {query.isPending ? (
          <Spinner label="Loading supplier details…" />
        ) : query.isError ? (
          <SupplierViewError error={query.error} />
        ) : (
          <SupplierViewBody
            data={query.data}
            runId={id}
            supplierName={query.data.supplier_name}
            customerSlug={run.data?.customer_slug ?? null}
          />
        )}
      </div>
    </div>
  );
}

/** /runs/$id/suppliers/by-id/$supplierId — id-keyed supplier view. The
 *  preferred entry point; the name never rides the URL. */
export function SupplierViewByIdPage() {
  const { id, supplierId } = useParams({
    from: '/runs/$id/suppliers/by-id/$supplierId',
  });
  return <SupplierViewContent id={id} supplierId={supplierId} />;
}

/** /runs/$id/suppliers/$supplierName — name-keyed fallback for suppliers
 *  with no `suppliers` row id. TanStack Router URL-decodes path params, so
 *  `supplierName` is already the raw human-readable name. */
export function SupplierViewPage() {
  const { id, supplierName } = useParams({
    from: '/runs/$id/suppliers/$supplierName',
  });
  return <SupplierViewContent id={id} supplierNameFromUrl={supplierName} />;
}
